import React from 'react';
import Tooltip from '@atlaskit/tooltip';
import { expVal } from '@atlassian/jira-feature-experiments';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import type { IntlShapeV2 } from '@atlassian/jira-intl/src/v2/types.tsx';
import { LabelsPicker } from '@atlassian/jira-label-picker/src/index.tsx';
import InlineErrorMessage from '@atlassian/jira-project-settings-apps-common/src/inline-error-message/view.tsx';
import { preventFormSubmitOnEnter as preventFormSubmit } from '@atlassian/jira-project-settings-apps-common/src/util/form/index.tsx';
import type { FieldConfigurationComponentProps } from '../../../../types.tsx';
import type { AssociatedField } from '../../../../types/layout-items.tsx';
import type { ValidationError } from '../../../../types/validation.tsx';
import { POSITION_FIELD_DEFAULT_VALUE_INPUT } from '../../../../utils/validation/constants.tsx';
import TestIdWrapper from '../../common/test-wrapper/index.tsx';
import { AkFieldWrapper, FieldConfigurationDefaultValue } from '../../styled.tsx';
import messages from './messages.tsx';

const onDefaultValueChange =
	(field: AssociatedField, onUpdate: (field: AssociatedField) => void) => (labels: string[]) =>
		onUpdate({
			...field,
			defaultValue: labels.length === 0 ? null : labels.join(','),
		});

export const splitLabels = (labelsString: string | null): string[] => {
	if (labelsString === null) {
		return [];
	}
	return labelsString
		.trim()
		.split(/[\s,]+/)
		.filter((label) => label.length > 0);
};

const getCustomFieldId = (fieldName: string) => {
	const found = fieldName.match('customfield_');
	if (found && found.length > 0) {
		return fieldName.replace('customfield_', '');
	}

	return undefined;
};

type FilteredValidationError = ValidationError & {
	position: typeof POSITION_FIELD_DEFAULT_VALUE_INPUT;
};

function isRelevantError(e: ValidationError): e is FilteredValidationError {
	return e.position === POSITION_FIELD_DEFAULT_VALUE_INPUT;
}

function getFilteredErrors(errors: ReadonlyArray<ValidationError>): FilteredValidationError[] {
	return errors.filter(isRelevantError);
}

const DefaultValueErrors = ({
	filteredErrors,
}: {
	filteredErrors: ReadonlyArray<FilteredValidationError>;
}) => {
	const intl = useIntl();
	if (filteredErrors.length === 0) {
		return null;
	}
	return (
		<>
			{filteredErrors.map((e) => (
				<InlineErrorMessage
					key={e.messageDescriptor.id}
					message={intl.formatMessage(e.messageDescriptor, e.values)}
				/>
			))}
		</>
	);
};

function refineDefaultValue(defaultValue: unknown): ReadonlyArray<string> {
	return splitLabels(typeof defaultValue === 'string' ? defaultValue : null);
}

export const LabelsFieldConfiguration = ({
	field,
	onUpdate,
	fieldErrors,
	tooltipDelay,
	testId,
}: FieldConfigurationComponentProps) => {
	const intl: IntlShape = useIntl();
	// @ts-expect-error LabelsPicker wants IntlShapeV2
	const intlV2: IntlShapeV2 = intl;
	const isModifyDefaultValueDisabled = !field.operations.canModifyDefaultValue;

	const filteredErrors: FilteredValidationError[] = getFilteredErrors(fieldErrors);
	const isInvalid: boolean = filteredErrors.length > 0;

	const defaultValue = refineDefaultValue(field.defaultValue);
	return (
		<FieldConfigurationDefaultValue>
			<AkFieldWrapper onKeyDown={preventFormSubmit}>
				<Tooltip
					content={
						isModifyDefaultValueDisabled
							? intl.formatMessage(messages.labelsDefaultValueDisabledTooltip)
							: null
					}
					delay={tooltipDelay}
				>
					<TestIdWrapper testId={testId}>
						<LabelsPicker
							value={defaultValue}
							noPortal
							baseUrl=""
							customFieldId={getCustomFieldId(field.key)}
							isDisabled={isModifyDefaultValueDisabled}
							isRequired={false}
							label={intl.formatMessage(messages.labelsDefaultValueLabel)}
							placeholder={intl.formatMessage(messages.labelsDefaultValuePickerPlaceholder)}
							// @ts-expect-error - TS2769 - No overload matches this call.
							onChange={onDefaultValueChange(field, onUpdate)}
							intl={intlV2}
							enableAnimation={false} // See https://donut-world-dogfood.atlassian.net/browse/MC-1971
							isInvalid={isInvalid}
							{...(expVal(
								'adding-color-to-labels-field-in-jira-experiment',
								'isColoredLabelsEnabled',
								false,
							)
								? { isColored: true }
								: {})}
						/>
					</TestIdWrapper>
				</Tooltip>
				<DefaultValueErrors filteredErrors={filteredErrors} />
			</AkFieldWrapper>
		</FieldConfigurationDefaultValue>
	);
};
